<template>
  <template v-if="!editMode">
    <v-chip @click="showEditor">
      {{ getText() }}
    </v-chip>
  </template>
  <template v-else>
    <v-combobox
      ref="editor"
      v-model:model-value="editorValue"
      :auto-select-first="onlyToolsAllowed"
      density="compact"
      :error-messages="buildErrorMessage()"
      flat
      focused
      hint="…"
      hide-details
      :items="filteredSuggestions"
      item-title="label"
      label="Combobox"
      :min-width="minEditorWidth"
      menu
      single-line
      style="display: inline-block"
      variant="plain"
      @keydown.left="moveToLeft()"
      @keydown.right="moveToRight()"
      @keydown.enter="leaveEditor()"
      @update:focused="
        (f: boolean) => {
          if (!f) {
            leaveEditor()
          }
        }
      "
      @vue:mounted="moveCursor(editAtEnd)"
      @vue:updated="moveCursor(editAtEnd)"
    ></v-combobox>
  </template>
</template>

<script setup lang="ts">
  import { computed, ref, useTemplateRef, watch } from 'vue'
  import { Filter, Tool } from '@/models/custom-metric.model'
  import { isNumber } from 'lodash'

  interface Props {
    part: Filter
    suggestions: Tool[]
    edit: boolean
  }
  const props = defineProps<Props>()
  const editMode = ref<boolean>(false)
  const editAtEnd = ref<boolean>()
  const editor = useTemplateRef('editor')
  const editorValue = ref<string | Tool>()
  const onlyToolsAllowed = computed(() => {
    const onlyTool = !props.suggestions.find(
      (tool: Tool) =>
        tool.kind == 'user_input' &&
        props.part.uuid &&
        tool.targets &&
        tool.targets.indexOf(props.part.uuid) != -1,
    )
    return onlyTool
  })
  const filteredSuggestions = computed(() =>
    props.suggestions
      .filter((s) => props.part.uuid && s.targets && s.targets.indexOf(props.part.uuid) != -1)
      .filter((s) => s.kind != 'user_input'),
  )
  const minEditorWidth = computed(() => {
    const width = Math.max(5, getText().length) + 'em'
    return width
  })
  watch(editorValue, () => {
    if (editorValue.value && typeof editorValue.value != 'string') {
      leaveEditor()
    }
  })

  const emit = defineEmits<{
    selectedTool: [tool: Tool, location: string]
    cursorLeaveLeft: [uuid: string]
    cursorLeaveRight: [uuid: string]
    fetchTools: [uuid: string]
  }>()

  function buildErrorMessage(): string | undefined {
    if (onlyToolsAllowed.value && props.suggestions.length == 0) {
      return 'You have to select an existing tool to change the value'
    }
    return undefined
  }

  function showEditor(): void {
    if (props.part.uuid) {
      editorValue.value = getText()
      editMode.value = true
      emit('fetchTools', props.part.uuid)
    }
  }

  function leaveEditor(): void {
    const newValue = editorValue.value
    if (!newValue) {
      // Update nothing
    } else if (typeof newValue == 'string') {
      stringReplacementEntered(newValue)
    } else if (props.part.uuid && newValue != null) {
      emit('selectedTool', newValue, props.part.uuid)
    }

    editMode.value = false
  }

  function getText() {
    if (!props.part) {
      return ''
    }
    switch (props.part.type) {
      case 'and':
        return 'AND'
      case 'contains':
        return 'contains'
      case 'eq':
        return '=='
      case 'ne':
        return '!='
      case 'literal':
        return props.part.value
      case 'toolbox.assist':
        return ''
      case 'toolbox.assist.binary':
        return ''
      case 'field':
        switch (props.part.field.type) {
          case 'toolbox.assist':
            return ''
          case 'artifact.property':
            return (
              props.part.field.property +
              (props.part.field.index === 0 || props.part.field.index === undefined
                ? ''
                : `(${props.part.field.index})`)
            )
          case 'artifact.attribute':
            return (
              props.part.field.attribute +
              (props.part.field.index === 0 || props.part.field.index === undefined
                ? ''
                : `(${props.part.field.index})`)
            )
        }
    }
  }
  function stringReplacementEntered(newValue: string) {
    const tool = props.suggestions.find((tool) => tool.kind == 'user_input')
    if (tool && props.part.uuid) {
      tool.value = newValue
      emit('selectedTool', tool, props.part.uuid)
    }
  }

  function moveToLeft() {
    if (!editor.value) {
      throw Error('Operator must be set whenever the cursor is visible.')
    }
    let newIndex = -1
    if (isNumber((editor.value as any).selectionStart)) {
      newIndex = (editor.value as any).selectionStart
    }
    if (newIndex <= 0 && props.part.uuid) {
      editMode.value = false
      emit('cursorLeaveLeft', props.part.uuid)
    }
  }
  function moveToRight() {
    if (!editor.value) {
      throw Error('Operator must be set whenever the cursor is visible.')
    }
    let newIndex = -1
    if (isNumber((editor.value as any).selectionStart)) {
      newIndex = (editor.value as any).selectionStart
    }
    if (newIndex == editorValue.value?.toString().length && props.part.uuid) {
      editMode.value = false
      emit('cursorLeaveRight', props.part.uuid)
    }
  }

  function moveCursor(toEnd?: boolean) {
    if (editor.value && isNumber((editor.value as any).selectionStart)) {
      if (toEnd === undefined) {
        // Do nothing
      } else if (toEnd) {
        ;(editor.value as any).selectionStart = getText().length
      } else {
        ;(editor.value as any).selectionStart = 0
      }
    }
  }
</script>

<style scoped>
  .v-item-group {
    flex-wrap: nowrap !important;
    flex-direction: row !important;
    display: flex !important;
    flex-flow: row !important;
  }
  .inline-header-container {
    display: inline-flex;
    align-items: center;
  }

  .edit-icon {
    margin-left: 10px; /* Add space between the text and the icon */
  }

  .filter-name-input {
    max-width: 400px;
  }

  .primary-btn {
    background-color: rgb(var(--v-theme-cyan-dark));
    color: rgb(var(--v-theme-surface));
  }

  .secondary-btn {
    background-color: rgb(var(--v-theme-itemis-hidden-grey));
    color: rgb(var(--v-theme-surface));
  }

  .btn-group {
    height: 2.5rem;
  }
  .toggle-btn {
    background-color: rgb(var(--v-theme-cyan-dark));
    color: rgb(var(--v-theme-surface));
    margin: 0 !important;
    padding: 1px !important;
    width: auto;
  }

  .chevron-down {
    transform: rotate(0deg);
    transition: transform 0.2s ease-in-out;
  }
  .chevron-up {
    transform: rotate(180deg);
    transition: transform 0.2s ease-in-out;
  }
</style>
